import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { travelAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getEventTypeLabel } from '../../types/event';
import type { TravelCopyCandidate } from '../../types/travel';
import { formatTravelTime } from '../../types/travel';
import { getErrorMessage } from '../../utils/errorMessage';

interface TravelCopyModalProps {
  eventId: number;
  eventTitle: string;
  onClose: () => void;
}

/**
 * Az utazás (a stáb járművei a megállókkal, és ha kérik, az utasok) átmásolása a
 * korosztály közelgő eseményeire - pl. a heti edzésekre járó kisbusz egyszeri
 * beállítás után. A megállók időpontja az adott eseményhez igazodik.
 */
const TravelCopyModal: React.FC<TravelCopyModalProps> = ({ eventId, eventTitle, onClose }) => {
  const { showToast } = useToast();
  const [targets, setTargets] = useState<TravelCopyCandidate[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [includePassengers, setIncludePassengers] = useState(true);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    travelAPI
      .getCopyTargets(eventId)
      .then((list) => {
        setTargets(list);
        // Ugyanaz a nap és időpont: jó eséllyel ugyanaz a sorozat
        setSelected(new Set(list.filter((t) => t.sameSlot && !t.hasVehicles).map((t) => t.eventId)));
      })
      .catch(() => {
        setTargets([]);
        showToast('Az események betöltése nem sikerült.', 'error');
      });
  }, [eventId, showToast]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCopy = async () => {
    setCopying(true);
    try {
      const result = await travelAPI.copyTravel(eventId, Array.from(selected), includePassengers);
      const skipped = result.skipped > 0 ? ` ${result.skipped} eseményt kihagytam, mert ott már van jármű.` : '';
      showToast(`Átmásolva ${result.copied} eseményre.${skipped}`);
      onClose();
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A másolás nem sikerült.'), 'error');
    } finally {
      setCopying(false);
    }
  };

  return (
    <Dialog open onClose={onClose} className="relative z-[60]">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-h-[85vh] md:max-w-lg md:rounded-lg">
          <div className="flex shrink-0 items-start justify-between gap-2 border-b p-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Utazás másolása</Dialog.Title>
              <p className="truncate text-sm text-gray-500">Innen: {eventTitle}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Bezárás"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            <p className="text-sm text-gray-600">
              A stáb járművei a megállókkal átkerülnek a kiválasztott eseményekre, a megállók időpontja az adott
              esemény kezdéséhez igazodik. A szülők felajánlott autói nem másolódnak.
            </p>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includePassengers}
                onChange={(e) => setIncludePassengers(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                Az utasokat is (aki ott is a keretben van és nem mondta le). Ők - és a szüleik - értesítést kapnak.
              </span>
            </label>

            {targets === null ? (
              <p className="py-6 text-center text-sm text-gray-500">Betöltés...</p>
            ) : targets.length === 0 ? (
              <p className="rounded-md bg-gray-50 p-4 text-center text-sm text-gray-500">
                A következő 60 napban nincs másik eseménye a korosztálynak.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                {targets.map((t) => (
                  <li key={t.eventId}>
                    <label
                      className={`flex items-center gap-3 px-3 py-2 ${t.hasVehicles ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(t.eventId)}
                        onChange={() => toggle(t.eventId)}
                        disabled={t.hasVehicles}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">{t.eventTitle}</span>
                        <span className="block text-xs text-gray-500">
                          {formatTravelTime(t.eventStart, true)} · {getEventTypeLabel(t.eventType)}
                          {t.hasVehicles && ' · már van utazása'}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t p-4 pb-safe-4">
            <button
              type="button"
              onClick={onClose}
              disabled={copying}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="button"
              onClick={handleCopy}
              disabled={copying || selected.size === 0}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {copying ? 'Másolás...' : `Másolás (${selected.size})`}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default TravelCopyModal;
